import * as React from 'react';
import { useLazyQuery } from "@apollo/react-hooks";
import gql from "graphql-tag";
import { AsyncLoadPagedResult, AsyncLoadPagedOptions } from '../../../shared/abstractStore';
import { useFetchMoreCallback } from '../../../shared/useFetchMoreCallback';
import { subscriptionDepartmentsQuery, subscriptionDepartmentsQueryVariables } from '../generated/subscriptionDepartmentsQuery';
import { mainApiConfig } from '../../../configure/mainApiConfig';
import { subscriptionDepartmentFieldsFragment } from '../models/SubscriptionDepartment';

export interface subscriptionDepartmentsAsyncLoadPageOptions extends AsyncLoadPagedOptions {
    subscriptionId?: string | null | undefined,
}

/**
 * Get a list of subscriptionDepartments from the store.
 * @param id
 * @param options
 */
export function useSubscriptionDepartments(options: subscriptionDepartmentsAsyncLoadPageOptions = {}): AsyncLoadPagedResult<subscriptionDepartmentsQuery> {
    // Query data we need from the api.
    const [load, { data, loading, error, refetch, fetchMore: fetchMoreFromStore }] = useLazyQuery<subscriptionDepartmentsQuery, subscriptionDepartmentsQueryVariables>(
        gql`
        query subscriptionDepartmentsQuery ($offset: Int, $first: Int, $subscriptionId: ID) {
            items: subscriptionDepartments (offset: $offset, first: $first, subscriptionId: $subscriptionId) {
                ...subscriptionDepartmentFields
            }
        }

        ${subscriptionDepartmentFieldsFragment}
        `,
        {

            variables: {
                offset: options.startOffset,
                first: options.pageSize,
                subscriptionId: options.subscriptionId,
           },
            fetchPolicy: options.fetchPolicy ?? mainApiConfig.defaultFetchPolicy,
            pollInterval: options.pollInterval,
            onCompleted: (data) => {
                if (options.onCompleted) {
                    options.onCompleted(data);
                }
            }
        }
    );

    // Run the query (unless we are being lazy).
    React.useEffect(() => {
        if (!options.lazy) {
            load();
        }
    }, [options.lazy, load])

    // Get the functions used to manage paging.
    const { fetchMore, hasMore: _hasMore } = useFetchMoreCallback<subscriptionDepartmentsQuery>(
        fetchMoreFromStore,
        (options?.startOffset ?? 0) + (data?.items?.length ?? 0),
        (prevResults, newResults) => ({
            ...prevResults,
            ...newResults,

            items: [...(prevResults?.items ?? []), ...(newResults?.items ?? [])]
        }),
        (newResults) => newResults?.items?.length
    );
    const hasMore = React.useCallback(() => !options.pageSize ? false : _hasMore(), [options.pageSize, _hasMore]);
    
    // Return the results.
    const refresh = React.useCallback(async () => { !refetch ? await load() : await refetch(); }, [refetch, load]);
    return React.useMemo(() => ({
        data: data ?? ({} as any),
        refresh: refresh,
        isLoading: loading,
        errors: error,
        fetchMore: fetchMore,
        hasMore: hasMore,
    }), [data, refresh, loading, error, fetchMore, hasMore]);
}
